import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  MteBase,
  MteWasm,
  MteStatus,
  MteMkeEnc,
  MteMkeDec,
  MteEnc,
  MteDec,
  MteFlenEnc,
} from "mte";
import { firstValueFrom } from 'rxjs';

export let mteWasm: MteWasm;
export let mteBase: MteBase;

const FIXED_LENGTH = 1000;
/**
   * Elliptic Curve Diffie Hellman options to use.
   * https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/deriveKey#supported_algorithms
   * Default options for the namedCurve are P-256, P-384, P-521. That's it.
   * TODO: explore other curve options such as Bernstein's Curve25519, Curve41417, or E-521.
   */
export const CURVE = {
  name: 'ECDH',
  namedCurve: 'P-256',
};

const NUM_BITS_OF_SECRET: number = 256;

// This is transmitted to the backend.
// The "public keys" are somewhat of a misnomer.
// With ECDH, the "private key" of backend and frontend are
// integers a and b, resp. (i.e. not keys since they don't decrypt anything)
// A fixed elliptic curve point P is hardcoded by the standard.
// The corresponding "public keys" are aP and bP,
// i.e. not keys since they don't encrypt anything.
// The shared secret is derived from a*(bP) = abP = b*(aP),
interface PublicKeys {
  decoder_ecdh_pubkey: string; // base-64 encoded.
  encoder_ecdh_pubkey: string; // base-64 encoded.
}

class FrontendPubKeys implements PublicKeys {
  decoder_ecdh_pubkey: string;
  encoder_ecdh_pubkey: string;
  personalization_string: string;
  locale?: string

  constructor(decoder_ecdh_pubkey: string, encoder_ecdh_pubkey: string, personalization_string: string, locale?: string) {
    this.decoder_ecdh_pubkey = decoder_ecdh_pubkey
    this.encoder_ecdh_pubkey = encoder_ecdh_pubkey
    this.personalization_string = personalization_string
    this.locale = locale
  }
}

class BackendPubKeys implements PublicKeys {
  decoder_ecdh_pubkey: string; // base-64 encoded.
  encoder_ecdh_pubkey: string; // base-64 encoded.
  nonce: string; // a timestamp

  constructor(decoder_ecdh_pubkey: string, encoder_ecdh_pubkey: string, nonce: string) {
    this.decoder_ecdh_pubkey = decoder_ecdh_pubkey
    this.encoder_ecdh_pubkey = encoder_ecdh_pubkey
    this.nonce = nonce
  }
}

// Entropy: $K = abP \in E(\F_p)$ from the output of ECDH.
// Both entropy sources are base-64 strings.
export interface MteInitialization {
  decoderEntropy: Uint8Array;
  encoderEntropy: Uint8Array;
  personalization_string: string;
  nonce: string;
}

export interface mteStates {
  encoderState: string | null,
  decoderState: string | null
};

@Injectable({
  providedIn: 'root'
})
export class MteService {
  readonly useMte: boolean = environment.useMTE;
  readonly licenseCompany: string = "Voatz";

  clientMteStates: mteStates = {encoderState: "", decoderState: ""};

  clientDecoder: MteMkeDec | undefined;
  clientEncoder: MteMkeEnc | undefined;

  needHandshake: boolean = true;

  frontendPubKeys = new FrontendPubKeys("", "", "", "")
  backendPubKeys = new BackendPubKeys("", "", "")

  mteInitialization: MteInitialization = {
    decoderEntropy: new Uint8Array(),
    encoderEntropy: new Uint8Array(),
    personalization_string: "",
    nonce: ""
  }

  mteInitialized = signal(false);

  constructor(private http: HttpClient) {
    mteWasm = new MteWasm();
    mteWasm.instantiate().then( async () => {
      mteBase = new MteBase(mteWasm);

      let licenseInitVal = base64ToUInt8Array("3H7qT8oMMY8r3Vxgi+Ya5Q");
      const licenseKey = await this.getMteLicense(environment.mteInit, licenseInitVal);

      if (!mteBase.initLicense(this.licenseCompany, licenseKey)) {
        const licenseStatus = MteStatus.mte_status_license_error;
        const status = mteBase.getStatusName(licenseStatus);
        const message = mteBase.getStatusDescription(licenseStatus);
        throw new Error(`Error with MTE License.\n${status}: ${message}`);
      }
      this.mteInitialized.set(true);
    });
  }

  async getMteLicense(mteInit: string, licenseInitVal: Uint8Array): Promise<string> {
    let iVector: Uint8Array = base64ToUInt8Array("r/vKRJxnS9FfSdXj5wvvrg");

    const initValEnc = await crypto.subtle.importKey(
      "raw",
      licenseInitVal.buffer,
      "AES-CBC",
      false,
      ["encrypt", "decrypt"],
    );
    let licenseBuffer = await window.crypto.subtle.decrypt(
      {
        name: "AES-CBC",
        iv: iVector
      },
      initValEnc,
      base64ToUInt8Array(environment.mteInit)
    );
    return arrayBufferToBase64(licenseBuffer);
  }

  /**
 * Generates a pair of keys for Eclypses MTE: decoder keys and encoder keys.
 * ECDH keys, and returns a base64 representation of the public key, as well as a closure function that can consume a foreign public key and generate a shared secret.
 * @params id: an identification unique to the voter.
 * @returns Nothing.
 */

  async handshake(): Promise<boolean> {
    // Generate $a \in \N$ and $A = aP \in E(\F_p)$ for decoder and encoder.
    // https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/generateKey
    // Both Keys are of type CryptoKeyPair, with .privateKey and .publicKey as CryptoKey objects.
    // This could be initialized earlier, asynchronously, in order to save a bit of time on the frontend.
    const decoderKeysPromise: Promise<CryptoKeyPair> = window.crypto.subtle.generateKey(CURVE, false, [
      'deriveBits',
    ]);
    const encoderKeysPromise: Promise<CryptoKeyPair> = window.crypto.subtle.generateKey(CURVE, false, [
      'deriveBits',
    ]);
    const [decoderKeys, encoderKeys] = await Promise.all([decoderKeysPromise, encoderKeysPromise]);
    this.frontendPubKeys = await this.initFrontendPubKeys(decoderKeys, encoderKeys);

    try {
      let pubKeys = await firstValueFrom(this.http.post<BackendPubKeys>(
        '/api/handshake',
        this.frontendPubKeys,
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'x-client-id': this.frontendPubKeys.personalization_string
          }),
          observe: 'body',
          responseType: 'json'
        }
      ));
    
      if(pubKeys) {
        this.backendPubKeys = pubKeys;
      } else {
        return Promise.reject("Backend public key is missing.");
      }
      this.mteInitialization = await this.computeSharedEntropy(decoderKeys, encoderKeys, this.frontendPubKeys, pubKeys);

      this.clientMteStates = this.createInitialMteState(
        this.mteInitialization.encoderEntropy,
        this.mteInitialization.nonce,
        this.mteInitialization.personalization_string,
        this.mteInitialization.decoderEntropy,
        this.mteInitialization.nonce,
        this.mteInitialization.personalization_string
      );
      if(!(this.clientMteStates.encoderState && this.clientMteStates.decoderState)){
        return Promise.reject("Decoder or encoder state is missing.");
      }

      this.clientEncoder = MteMkeEnc.fromdefault(mteWasm);
      this.clientDecoder = MteMkeDec.fromdefault(mteWasm);
      
      this.clientEncoder.restoreStateB64(this.clientMteStates.encoderState);
      this.clientDecoder.restoreStateB64(this.clientMteStates.decoderState);
      this.needHandshake = false;
      return true;
    } catch(err) {
      console.error(err);
      return false;
    }
  }

  async computeSharedEntropy(
    decoderKeys: CryptoKeyPair,
    encoderKeys: CryptoKeyPair,
    frontendPubKeys: FrontendPubKeys,
    backendPubKeys: BackendPubKeys
  ): Promise<MteInitialization> {

    const decoderSecretPromise = this.computeSharedSecret( decoderKeys.privateKey, backendPubKeys.encoder_ecdh_pubkey );
    const encoderSecretPromise = this.computeSharedSecret( encoderKeys.privateKey, backendPubKeys.decoder_ecdh_pubkey );
    return Promise.all([decoderSecretPromise, encoderSecretPromise]).then( ([decoderSecret, encoderSecret]) => {
      return {
        decoderEntropy: decoderSecret,
        encoderEntropy: encoderSecret,
        personalization_string: frontendPubKeys.personalization_string,
        nonce: backendPubKeys.nonce
      }
    })
  }

  /**
   * Compute the shared secret from the frontend's private $a$ and the backend's public $B = bP$,
   * by computing $K = abP$ and deriving 256 bits of entropy from the resulting point.
   * @params public_key: base 64 string, private_key: base 64 string
   * @return shared_secret: Uint8Array
   */
  private async computeSharedSecret(private_a: CryptoKey, public_bP: string): Promise<Uint8Array> {
    const pubKeyU8: Uint8Array = base64ToUInt8Array(public_bP);
    const importedPublicKey: CryptoKey = await window.crypto.subtle.importKey(
      "raw",
      pubKeyU8,
      CURVE,
      true,
      []
    );
    const sharedSecretArray: ArrayBuffer = await window.crypto.subtle.deriveBits(
      { name: CURVE.name, public: importedPublicKey },
      private_a,
      NUM_BITS_OF_SECRET
    );
    return new Uint8Array(sharedSecretArray);
  }

  async initFrontendPubKeys(decoderKeys: CryptoKeyPair, encoderKeys: CryptoKeyPair, locale?: string): Promise<FrontendPubKeys> {
    const decoderPublicKeyPromise = window.crypto.subtle.exportKey('raw', decoderKeys.publicKey);
    const encoderPublicKeyPromise = window.crypto.subtle.exportKey('raw', encoderKeys.publicKey);

    // Note: window.crypto.randomUUID() is only available in Typescript v. >=4.6.
    const personalizationString = randomUUID();

    return Promise.all([decoderPublicKeyPromise, encoderPublicKeyPromise]).then( ([decoderPublicKey, encoderPublicKey]) => {
      return {
        decoder_ecdh_pubkey: arrayBufferToBase64(decoderPublicKey),
        encoder_ecdh_pubkey: arrayBufferToBase64(encoderPublicKey),
        personalization_string: personalizationString,
        locale: locale
      };
    })
  }
  /**
       * Use MTE to encode a string
       * - create mte encoder, restore it from saved mte state
       * - encode string
       * - save new encoder state, destroy encoder
       * - return encoded result
       */
  mteEncodeString(str: string) {
    const encoder = MteEnc.fromdefault(mteWasm);
    if (this.clientMteStates.encoderState) {
      encoder.restoreStateB64(this.clientMteStates.encoderState);
    }
    const encodeResult = encoder.encodeStrB64(str);
    if (encodeResult.status !== MteStatus.mte_status_success && mteBase) {
      const status = mteBase.getStatusName(encodeResult.status);
      const message = mteBase.getStatusDescription(encodeResult.status);
      throw new Error(`Error when MTE encoding string.\n${status}: ${message}`);
    }
    this.clientMteStates.encoderState = encoder.saveStateB64();
    encoder.uninstantiate();
    encoder.destruct();
    return encodeResult.str;
  }

  /**
   * Use MKE to encode a string
   * - create MKE default encoder, restore it from saved mte state
   * - encode string
   * - save new encoder state, destroy encoder
   * - return encoded result
   */
  mkeEncryptString(str: string) {
    if (!this.clientEncoder) {
      this.clientEncoder = MteMkeEnc.fromdefault(mteWasm);
    }
    if (!this.clientMteStates.encoderState) {
      throw new Error("Could not retrieve encoder state.");
    }
    this.clientEncoder.restoreStateB64(this.clientMteStates.encoderState);
    const encodeResult = this.clientEncoder.encodeStrB64(str);
    if (encodeResult.status !== MteStatus.mte_status_success && mteBase) {
      const status = mteBase.getStatusName(encodeResult.status);
      const message = mteBase.getStatusDescription(encodeResult.status);
      throw new Error(`Error when MKE encoding string.\n${status}: ${message}`);
    }
    this.clientMteStates.encoderState = this.clientEncoder.saveStateB64();
    return encodeResult.str;
  }
  /**
   * Use MKE to decode a string
   * - create MKE default decoder, restore it from saved mte state
   * - decode string
   * - save new decoder state, destroy decoder
   * - return decoded result
   */
  mkeDecryptB64(b64: string) {
    if (!this.clientDecoder) {
      this.clientDecoder = MteMkeDec.fromdefault(mteWasm);
    }
    if(!this.clientMteStates.decoderState) {
      throw new Error("Could not retrieve decoder state.");
    }
    this.clientDecoder.restoreStateB64(this.clientMteStates.decoderState);
    const decodeResult = this.clientDecoder.decodeStrB64(b64);
    if (decodeResult.status !== MteStatus.mte_status_success && mteBase) {
      const status = mteBase.getStatusName(decodeResult.status);
      const message = mteBase.getStatusDescription(decodeResult.status);
      throw new Error(`Error when MKE decoding string.\n${status}: ${message}`);
    }
    this.clientMteStates.decoderState = this.clientDecoder.saveStateB64();
    return decodeResult.str;
  }

  /**
   * Use MTE Fixed-Length to encode a string
   * - create mte fixed-length encoder, restore it from saved mte state
   * - encode string
   * - save new encoder state, destroy encoder
   * - return encoded result
   */
  mteFlenEncodeString(str: string) {
    const encoder = MteFlenEnc.fromdefault(mteWasm, FIXED_LENGTH);
    if (this.clientMteStates.encoderState) {
      encoder.restoreStateB64(this.clientMteStates.encoderState);
    }
    const encodeResult = encoder.encodeStrB64(str);
    if (encodeResult.status !== MteStatus.mte_status_success && mteBase) {
      const status = mteBase.getStatusName(encodeResult.status);
      const message = mteBase.getStatusDescription(encodeResult.status);
      throw new Error(
        `Error when MTE Fixed-Length encoding data.\n${status}: ${message}`
      );
    }
    this.clientMteStates.encoderState = encoder.saveStateB64();
    encoder.uninstantiate();
    encoder.destruct();
    return encodeResult.str;
  }

  /**
   * Use MKE to encrypt a uint8Array
   * - create mte encoder, restore it from saved mte state
   * - encode string
   * - save new encoder state, destroy encoder
   * - return encoded result
   */
  mkeEncryptUint8Array(arr: Uint8Array) {
    const encoder = MteMkeEnc.fromdefault(mteWasm);
    if (this.clientMteStates.encoderState) {
      encoder.restoreStateB64(this.clientMteStates.encoderState);
    }
    const encodeResult = encoder.encode(arr);
    if (encodeResult.status !== MteStatus.mte_status_success && mteBase) {
      const status = mteBase.getStatusName(encodeResult.status);
      const message = mteBase.getStatusDescription(encodeResult.status);
      throw new Error(`Error when MKE encrypting data.\n${status}: ${message}`);
    }
    this.clientMteStates.encoderState = encoder.saveStateB64();
    encoder.uninstantiate();
    encoder.destruct();
    return encodeResult.arr;
  }

  /**
   * Use MTE to decode a B64 string
   * - create mte decoder, restore it from saved mte state
   * - decode b64 string
   * - save new decoder state, destroy decoder
   * - return decoded result
   */
  mteDecodeB64(b64: string) {
    const decoder = MteDec.fromdefault(mteWasm);
    if (this.clientMteStates.decoderState) {
      decoder.restoreStateB64(this.clientMteStates.decoderState);
    }
    const decodeResult = decoder.decodeStrB64(b64);
    if (decodeResult.status !== MteStatus.mte_status_success && mteBase) {
      const status = mteBase.getStatusName(decodeResult.status);
      const message = mteBase.getStatusDescription(decodeResult.status);
      throw new Error(`Error when MTE decoding data.\n${status}: ${message}`);
    }
    this.clientMteStates.decoderState = decoder.saveStateB64();
    decoder.uninstantiate();
    decoder.destruct();
    return decodeResult.str;
  }

  /**
   * Use MTE to decode a UintArray
   * - create mte decoder, restore it from saved mte state
   * - decode UintArray
   * - save new decoder state, destroy decoder
   * - return decoded result as a Uint8Array
   */
  mkeDecryptUint8Array(arr: Uint8Array) {
    const decoder = MteMkeDec.fromdefault(mteWasm, 10000, -63);
    if(this.clientMteStates.decoderState) {
      decoder.restoreStateB64(this.clientMteStates.decoderState);
    }
    const decodeResult = decoder.decode(arr);
    if (decodeResult.status !== MteStatus.mte_status_success && mteBase) {
      const status = mteBase.getStatusName(decodeResult.status);
      const message = mteBase.getStatusDescription(decodeResult.status);
      throw new Error(`Error when MKE decrypting data.\n${status}: ${message}`);
    }
    this.clientMteStates.decoderState = decoder.saveStateB64();
    decoder.uninstantiate();
    decoder.destruct();
    return decodeResult.arr;
  }

  /**
       * Initialize MTE encoder/decoder and return state
       * @param {Uint8Array} encoderEntropy A Uint8Array for the encoder
       * @param {string} encoderNonce A string of integers
       * @param {string} encoderPersonalization The personalization string
       * @param {Uint8Array} decoderEntropy A Uint8Array from the decoder
       * @param {string} decoderNonce A string of integers
       * @param {string} decoderPersonalization The personalization string
       */
  createInitialMteState(
    encoderEntropy: Uint8Array,
    encoderNonce: string,
    encoderPersonalization: string,
    decoderEntropy: Uint8Array,
    decoderNonce: string,
    decoderPersonalization: string
  ): mteStates {
    // create encoder
    const mteEncoder = MteEnc.fromdefault(mteWasm);
    mteEncoder.setEntropyArr(encoderEntropy);
    mteEncoder.setNonce(encoderNonce);
    const encoderStatus = mteEncoder.instantiate(encoderPersonalization);
    if (encoderStatus !== MteStatus.mte_status_success && mteBase) {
      const status = mteBase.getStatusName(encoderStatus);
      const message = mteBase.getStatusDescription(encoderStatus);
      throw new Error(`Error instantiating MTE Encoder.\n${status}: ${message}`);
    }
    // get mte encoder state
    let encoderState = mteEncoder.saveStateB64();
    // destroy encoder
    mteEncoder.uninstantiate();
    mteEncoder.destruct();

    // create decoder
    const mteDecoder = MteEnc.fromdefault(mteWasm);
    mteDecoder.setEntropyArr(decoderEntropy);
    mteDecoder.setNonce(decoderNonce);
    const decoderStatus = mteDecoder.instantiate(decoderPersonalization);
    if (decoderStatus !== MteStatus.mte_status_success && mteBase) {
      const status = mteBase.getStatusName(decoderStatus);
      const message = mteBase.getStatusDescription(decoderStatus);
      throw new Error(`Error instantiating MTE Decoder.\n${status}: ${message}`);
    }
    // save mte decoder state
    let decoderState = mteDecoder.saveStateB64();
    // destroy decoder
    mteDecoder.uninstantiate();
    mteDecoder.destruct();

    return { encoderState: encoderState, decoderState: decoderState };
  }
}

/**
* Utility to convert a array buffer to a base64 string.
* @param buffer A typed array.
* @returns A base64 encoded string of the original array buffer data.
*/
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = '';
  let bytes = new Uint8Array(buffer);
  let len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

// convert a base64 encoded string to a Uint8Array
function base64ToUInt8Array(base64Str: string) {
  const str = window.atob(base64Str);
  let i = 0;
  const iMax = str.length;
  const u8 = new Uint8Array(iMax);
  for (; i < iMax; ++i) {
    u8[i] = str.charCodeAt(i);
  }
  return u8;
}

// Create the personalization string, a UUID created from the voter ID.
//async createUUID(id: string): Promise<string> {
//  return uuidv5(id, this.NAMESPACE);
//}
// Modified from:
// https://github.com/uuidjs/randomUUID/blob/0763fd0ae69b71a8c0582f277109b97c5c5d73d5/randomUUID.js
export function randomUUID(): string {
  const kHexDigits = [48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 97, 98, 99, 100, 101, 102];
  let uuid: Uint8Array = new Uint8Array(36);
  uuid[8] = uuid[13] = uuid[18] = uuid[23] = '-'.charCodeAt(0);
  uuid[14] = 52; // '4', identifies the UUID version


  let uuidBuf = window.crypto.getRandomValues(new Uint8Array(16));

  // Variant byte: 10xxxxxx (variant 1)
  uuidBuf[8] = (uuidBuf[8] & 0x3f) | 0x80;

  // This function is structured the way it is for performance.
  // The uuid buffer stores the serialization of the random
  // bytes from uuidData.
  // xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  let n = 0;
  uuid[0] = kHexDigits[uuidBuf[n] >> 4];
  uuid[1] = kHexDigits[uuidBuf[n++] & 0xf];
  uuid[2] = kHexDigits[uuidBuf[n] >> 4];
  uuid[3] = kHexDigits[uuidBuf[n++] & 0xf];
  uuid[4] = kHexDigits[uuidBuf[n] >> 4];
  uuid[5] = kHexDigits[uuidBuf[n++] & 0xf];
  uuid[6] = kHexDigits[uuidBuf[n] >> 4];
  uuid[7] = kHexDigits[uuidBuf[n++] & 0xf];
  // -
  uuid[9] = kHexDigits[uuidBuf[n] >> 4];
  uuid[10] = kHexDigits[uuidBuf[n++] & 0xf];
  uuid[11] = kHexDigits[uuidBuf[n] >> 4];
  uuid[12] = kHexDigits[uuidBuf[n++] & 0xf];
  // -
  // 4, uuid[14] is set already...
  uuid[15] = kHexDigits[uuidBuf[n++] & 0xf];
  uuid[16] = kHexDigits[uuidBuf[n] >> 4];
  uuid[17] = kHexDigits[uuidBuf[n++] & 0xf];
  // -
  uuid[19] = kHexDigits[uuidBuf[n] >> 4];
  uuid[20] = kHexDigits[uuidBuf[n++] & 0xf];
  uuid[21] = kHexDigits[uuidBuf[n] >> 4];
  uuid[22] = kHexDigits[uuidBuf[n++] & 0xf];
  // -
  uuid[24] = kHexDigits[uuidBuf[n] >> 4];
  uuid[25] = kHexDigits[uuidBuf[n++] & 0xf];
  uuid[26] = kHexDigits[uuidBuf[n] >> 4];
  uuid[27] = kHexDigits[uuidBuf[n++] & 0xf];
  uuid[28] = kHexDigits[uuidBuf[n] >> 4];
  uuid[29] = kHexDigits[uuidBuf[n++] & 0xf];
  uuid[30] = kHexDigits[uuidBuf[n] >> 4];
  uuid[31] = kHexDigits[uuidBuf[n++] & 0xf];
  uuid[32] = kHexDigits[uuidBuf[n] >> 4];
  uuid[33] = kHexDigits[uuidBuf[n++] & 0xf];
  uuid[34] = kHexDigits[uuidBuf[n] >> 4];
  uuid[35] = kHexDigits[uuidBuf[n] & 0xf];

  return new TextDecoder().decode(uuid);
}

